/**
 * Currency formatting utilities for Indian Rupee (INR)
 * Uses Lakhs/Crores format for large numbers (tier 3 market friendly)
 */

/**
 * Format currency in compact Indian notation (L for Lakhs, Cr for Crores)
 * Values below 1 Lakh show full format
 *
 * @param {number} value - Amount in rupees
 * @param {string} locale - Locale for number formatting (default: 'en-IN')
 * @returns {string} Formatted currency string
 *
 * @example
 * formatCompactCurrency(50000)     // "₹50,000"
 * formatCompactCurrency(100000)    // "₹1.00L"
 * formatCompactCurrency(4108860)   // "₹41.09L"
 * formatCompactCurrency(12500000)  // "₹1.25Cr"
 */
export function formatCompactCurrency(value, locale = 'en-IN') {
  if (!value || value === 0) return '₹0'

  const absValue = Math.abs(value)
  const sign = value < 0 ? '-' : ''

  // Crores (1 Cr = 10,000,000)
  if (absValue >= 10000000) {
    return `${sign}₹${(absValue / 10000000).toFixed(2)}Cr`
  }

  // Lakhs (1 L = 100,000)
  if (absValue >= 100000) {
    return `${sign}₹${(absValue / 100000).toFixed(2)}L`
  }

  // Below 1 Lakh - show full format
  return formatFullCurrency(value, locale)
}

/**
 * Format currency in full Indian notation with all digits
 *
 * @param {number} value - Amount in rupees
 * @param {string} locale - Locale for number formatting (default: 'en-IN')
 * @returns {string} Formatted currency string
 *
 * @example
 * formatFullCurrency(50000)     // "₹50,000"
 * formatFullCurrency(4108860)   // "₹41,08,860"
 */
export function formatFullCurrency(value, locale = 'en-IN') {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0)
}
